/* eslint-disable @reatom/unit-naming-rule */
import { useAction, useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { Button, InfoIcon, Switch, Tooltip } from '@blade/ui-kit'
import {
  authStateAtom,
  mfaEnabledAtom,
  otpQuickLoginEnabledAtom,
  reatomToggleOtpQuickLogin,
} from '@root/entities/auth'
import { startMfaSetupEvent } from '@root/features/auth/setup-otp/atoms'
import { DatabaseIcon, Lock2Icon, ModulesIcon } from '@root/shared/ui-kit'

import { settingScreenAtom } from '../atoms'

import { SettingsItem } from './settings-item'
import { SettingsSecurityConfirm } from './settings-security-confirm'
import { SettingsSocials } from './settings-socials'

export const SettingsSecurity: React.FC = () => {
  const [_, setScreen] = useAtom(settingScreenAtom)
  const toggleOtpModel = useMemo(() => reatomToggleOtpQuickLogin(), [])
  const [toggleOtpQuickLoginState] = useAtom(toggleOtpModel)
  const toggleOtpQuickLogin = useAction(toggleOtpModel.execute)

  const { t } = useTranslation()

  const [authState] = useAtom(authStateAtom)
  const [mfaEnabled] = useAtom(mfaEnabledAtom)
  const startMfaSetup = useAction(startMfaSetupEvent)
  const [otpQuickLoginEnabled] = useAtom(otpQuickLoginEnabledAtom)
  const [disableMfaOpen, setDisableMfaOpen] = useState(false)

  const isSettingsDisabled = authState.type !== 'LoggedIn'

  const handleChangePassword = () => setScreen('update-password')
  const handleChangeLocalPassword = () => setScreen('update-local-password')
  const handleSessions = () => {
    if (isSettingsDisabled) {
      return
    }
    setScreen('sessions')
  }
  const handleMfa = () =>
    mfaEnabled ? setDisableMfaOpen(true) : startMfaSetup()

  return (
    <Root>
      <Items>
        <SettingsItem
          icon={<Lock2Icon size={18} />}
          title={t('settings.password.item')}
          disabled={isSettingsDisabled}
          endContent={
            <ButtonStyled
              onClick={handleChangePassword}
              variant="secondary"
              size="xs"
              radius="rounded"
              disabled={isSettingsDisabled}
            >
              {t('common.change')}
            </ButtonStyled>
          }
        />
        <SettingsItem
          icon={<Lock2Icon size={18} />}
          title={t('settings.local-password.item')}
          disabled={isSettingsDisabled}
          endContent={
            <ButtonStyled
              onClick={handleChangeLocalPassword}
              variant="secondary"
              size="xs"
              radius="rounded"
              disabled={isSettingsDisabled}
            >
              {t('common.change')}
            </ButtonStyled>
          }
        />
        <SettingsItem
          icon={<DatabaseIcon size={18} />}
          title={t('mfa.two_factory_auth')}
          disabled={isSettingsDisabled}
          endContent={
            <ButtonStyled
              onClick={handleMfa}
              variant="secondary"
              size="xs"
              radius="rounded"
              disabled={isSettingsDisabled}
            >
              {mfaEnabled ? t('common.remove') : t('common.connect')}
            </ButtonStyled>
          }
        />
        <SettingsItem
          disabled={isSettingsDisabled}
          icon={<DatabaseIcon size={18} />}
          title={
            <>
              <LabelWithInfo>
                {t('settings.security.otp-quick-login')}

                <Tooltip
                  content={
                    <Trans
                      i18nKey={'settings.security.otp-quick-login.description'}
                    />
                  }
                >
                  <InfoIcon size={16} />
                </Tooltip>
              </LabelWithInfo>
            </>
          }
          endContent={
            <Switch
              disabled={toggleOtpQuickLoginState.loading}
              checked={!otpQuickLoginEnabled}
              onCheckedChange={toggleOtpQuickLogin}
            />
          }
        />
        <SettingsSocials />
        <SettingsItem
          icon={<ModulesIcon size={18} />}
          title={t('settings.active_sessions')}
          onClick={handleSessions}
          disabled={isSettingsDisabled}
        />
      </Items>
      <SettingsSecurityConfirm
        open={disableMfaOpen}
        onOpenChange={setDisableMfaOpen}
      />
    </Root>
  )
}

const Root = styled.div``

const Items = styled.div`
  padding: 14px;
  gap: 10px;

  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
`

const ButtonStyled = styled(Button)`
  height: 28px;
`

const LabelWithInfo = styled.div`
  display: grid;
  align-items: center;
  grid-auto-flow: column;
  gap: 4px;

  & svg {
    color: var(--text-10);
  }
`
